<template lang="pug">
	.app-core--list-view
		.flex-col.space(v-show="visible")
			.flex-row.justify.space-medium.wrap.lr-padding(v-if="showViewHeader", ref="header")
				.left.flex-row.space.align-center
					slot(name="left-actions")
				.right.flex-row.space-large.flex-1.justify-end.align-center
					slot(name="right-actions")
					DxButton(
						v-if="view == 'table' && hasFilters()",
						:activeStateEnabled="false",
						:focusStateEnabled="false",
						:disabled="disabled",
						text="Clear filters"
						icon="fas fa-times",
						@click="() => clearFilters()"
					)
					DxTextBox(
						v-if="searchVisible",
						v-model="searchString",
						placeholder='Search..',
						:buttons="['clear', searchButton]",
						valueChangeEvent="keyup",
						:showClearButton="true",
						:disabled="disabled",
					)
					DxButton(
						v-if="refreshVisible",
						:activeStateEnabled="false",
						:focusStateEnabled="false",
						:disabled="disabled",
						:icon="isLoading ? 'fas fa-circle-notch fa-spin' : 'refresh'",
						@click="e => { e.event.stopPropagation(); innerReload(); }"
					)
					DxButtonGroup(
						v-if="!hideViewsSelection"
						:class="{ 'lr-padding': pagesCount > 1 }"
						:items="views"
						:disabled="disabled",
						:selected-item-keys="[view]"
						key-expr="id"
						styling-mode="contained"
						@item-click="e => view = e.itemData.id"
					)
					.flex-row.space-medium.align-center(v-if="topPagerVisible")
						Pager(
							v-if="topPagerVisible",
							:count="pagesCount",
							:pageIndex="pageIndex",
							:disabled="disabled",
							@change="onPageChange"
						)
						.flex-row.space.align-center.page-size-selector(v-if="pagesCount > 1 && showPageSize")
							.upper.grey.caption {{$tCaption('Page size')}}:
							DxSelectBox(:data-source="pageSizes", :value.sync="innerPageSize", :width="45", stylingMode="text", :dropDownOptions="pageSizesDropDownOptions")
			slot(name="top")
			.flex-row(:style="{ '--grid-height': `${gridHeight}px` }")
				slot(name="left")
				.flex-col.space.full-width.view-holder(ref="gridHolder")
					template(v-if="view === 'cards'")
						Loader(v-if="isLoading", text="", :fullscreen="true")
						.no-data.flex-row.full-width.full-height.align-center.center(v-if="cardsNoData")
							.font-small.upper.grey {{$tCaption('No data')}}
						DxScrollView(v-else, showScrollbar="always", :height="`${gridHeight}px`")
							.content
								.cards.flex-row.wrap(v-if="view == 'cards'")
									.card(v-for="item in items", :class="[cardCss]")
										slot(name="card" :data="item")
					.flex-row(v-if="view == 'table'")
						.flex-1.grid-holder-parent(:class="{ 'auto-fit': autoFit }")
							.grid-holder
								dx-data-grid(
									ref="table",
									v-if="gridWidth",
									:data-source="source",
									:show-borders="false"
									:column-hiding-enabled="columnHidingEnabled",
									:allowColumnResizing="true",
									:editing="editingSettings",
									:columns="gridColumns",
									:columnAutoWidth="columnAutoWidth",
									:keyExpr="valueExpr",
									:column-chooser="columnChooserSettings",
									:sorting="sortingSettings",
									:disabled="disabled",
									@cellClick="onCellClick",
									@selectionChanged="onSelectionChanged",
									@contentReady="onGridContentReady",
									:width="gridWidth",
									:height="gridHeight || '100%'",
									:showColumnHeaders="showColumnHeaders",
									:noDataText="noDataText ? noDataText : $tCaption('No data')",
									@row-prepared="e => $emit('row-prepared', e)",
									:keyboardNavigation="keyboardNavigationSettings"
								)
									dx-paging(:page-size="innerPageSize")
									dx-pager(:show-page-size-selector="true" :show-info="true", :visible="!remotePaging")
									dx-filter-row(:visible="filterRow")
									dx-filter-panel(:visible="filterPanel")
									dx-header-filter(:visible="headerFilter", :height="220")
									dx-selection(:deferred="false", :mode="selectionMode", showCheckBoxesMode="always", :selectAllMode="selectAllMode")
									dx-remote-operations(:sorting="remoteSorting", :paging="remotePaging", :groupPaging="remoteGrouping", :summary="remoteGrouping", :grouping="remoteGrouping", :filtering="remoteFiltering")
									template(v-for="(_, slot) of $scopedSlots" v-slot:[slot]="{ data }")
										slot(:name="slot" v-bind="data")
					slot(name="last-row")
					.flex-col.space(v-if="bottomPagerVisible", ref="pagerBottom")
						.flex-row.justify.space.wrap.lr-padding
							.left.flex-row.space.alig-center
								Pager(:count="pagesCount", :pageIndex="pageIndex", @change="onPageChange")
							.right.flex-row.space
								.flex-row.space.align-center.page-size-selector(v-if="!topPagerVisible && pagesCount > 1 && showPageSize")
									.upper.grey.caption {{$tCaption('Page size')}}:
									DxSelectBox(:data-source="pageSizes", :value.sync="innerPageSize", :width="45", stylingMode="text", :dropDownOptions="pageSizesDropDownOptions")
						.spacer
				slot(name="right")
			slot(name="bottom")
</template>

<script>
	import _ from 'lodash';
	import $ from 'jquery';
	import DxTextBox from 'devextreme-vue/text-box';
	import VueTypes from 'vue-types';
	import Pager from 'app-core/Pager.vue';
	import DxButton from 'devextreme-vue/button';
	import DxDataGrid, {
		DxColumn,
		DxFilterRow,
		DxFilterPanel,
		DxLookup,
		DxPager,
		DxPaging,
		DxHeaderFilter,
		DxSelection,
		DxRemoteOperations
	} from 'devextreme-vue/data-grid';
	import DxButtonGroup from 'devextreme-vue/button-group';
	import DataSource from 'devextreme/data/data_source';
	import Promise from 'bluebird';
	import resizeObserver from 'resize-observer';
	import Loader from 'app-core/Loader.vue';
	import DxScrollView from 'devextreme-vue/scroll-view';
	import DxSelectBox from 'devextreme-vue/select-box';
	import storage from 'storage';

	export default {
		components: {
			Pager, DxButton, DxButtonGroup, DxDataGrid, DxColumn, DxFilterRow, DxFilterPanel, DxLookup, DxPager,
			DxPaging, DxHeaderFilter, DxSelection, DxRemoteOperations, DxTextBox, DxScrollView, Loader, DxSelectBox
		},
		props: {
			getTotal: VueTypes.func.def(null),
			getItems: VueTypes.func.def(null),
			getData: VueTypes.func.def(null),
			columns: VueTypes.array.def(null),
			initialView: VueTypes.oneOf(['table', 'cards']).def('cards'),
			hideViewsSelection: VueTypes.bool.def(false),
			cardCss: VueTypes.string.def(null),
			pageSize: VueTypes.number.def(10),
			searchVisible: VueTypes.bool.def(false),
			name: VueTypes.string.def(null),
			columnHidingEnabled: VueTypes.bool.def(true),
			height: VueTypes.number.def(null),
			selectionMode: VueTypes.oneOf(['none', 'multiple', 'single']).def('none'),
			selectAllMode: VueTypes.oneOf(['allPages', 'page']).def('page'),
			showColumnHeaders: VueTypes.bool.def(true),
			showViewHeader: VueTypes.bool.def(true),
			filterRow: VueTypes.bool.def(false),
			headerFilter: VueTypes.bool.def(false),
			filterPanel: VueTypes.bool.def(false),
			disabled: VueTypes.bool.def(false),
			visible: VueTypes.bool.def(true),
			refreshVisible: VueTypes.bool.def(true),
			cache: VueTypes.bool.def(true),
			topPagerVisible: VueTypes.bool.def(true),
			bottomPagerVisible: VueTypes.bool.def(true),
			selectedRowKeys: VueTypes.array.def([]),
			valueExpr: VueTypes.string.def(null),
			remoteSorting: VueTypes.bool.def(true),
			remoteGrouping: VueTypes.bool.def(true),
			remotePaging: VueTypes.bool.def(true),
			remoteFiltering: VueTypes.bool.def(true),
			columnChooser: VueTypes.bool.def(false),
			noDataText: VueTypes.string.def(null),
			searchTimer: VueTypes.number.def(1000),
			columnAutoWidth: VueTypes.bool.def(true),
			heightDelta: VueTypes.number.def(20),
			autoFit: VueTypes.bool.def(false),
			keyboardNavigationDisabled: VueTypes.bool.def(false),
			showPageSize: VueTypes.bool.def(false),
			currentPage: VueTypes.number.def(0)
		},
		data() {
			return {
				source: new DataSource({
					load: (loadOptions) => {
						loadOptions = { ...(loadOptions || {}), sorting: this.getSorting() };
						if (this.searchString) {
							loadOptions.filter = {
								filterValue: this.searchString
							};
							loadOptions.searchString = this.searchString;
						}
						if (__DEBUG__)
							console.debug('Load list view data', {
								loadOptions,
								loading: this.isLoading,
								data: JSON.stringify(loadOptions),
								loadingPromise: this.loadingPromise
							});
						if (this.isLoading && this.loadingPromise) {
							//When using filters, load calls again before promise executes first time, and do infinite call again and again
							this.initializedInfiniteloadException = true;
							if (__DEBUG__)
								console.debug('Loading & promise');
							if (this.loadingPromise)
								return this.loadingPromise;
						} else if (this.initializedInfiniteloadException) {
							this.initializedInfiniteloadException = false;
							if (this.loadingPromise)
								return this.loadingPromise;
							if (__DEBUG__)
								console.debug('initializedInfiniteloadException');
						}
						if (this.cache && this.lastLoadedOpts && _.isEqual(this.lastLoadedOpts, loadOptions)) {
							this.loadingPromise = Promise.resolve({
								data: this.items || [],
								totalCount: this.total || 0
							});
							if (__DEBUG__)
								console.debug('cache', {
									old: JSON.stringify(this.lastLoadedOpts),
									new: JSON.stringify(loadOptions)
								});
						} else {
							this.loadingPromise = this.loadData(loadOptions).then(() => {
								if (__DEBUG__)
									console.debug('Data loaded', {items: this.items, total: this.total});

								return {
									data: this.items || [],
									totalCount: this.total || 0
								};
							}).finally(() => delete this.loadingPromise);
							if (__DEBUG__)
								console.debug('loadData');
						}
						return this.loadingPromise;
					},
					key: this.valueExpr
				}),
				loadingPromise: null,
				loadedResult: null,
				total: 0,
				items: [],
				pageIndex: this.currentPage,
				initialized: false,
				view: this.initialView,
				isLoading: false,
				views: [
					{
						icon: 'fas fa-bars',
						hint: 'Table',
						id: 'table'
					},
					{
						icon: 'fas fa-th',
						hint: 'Cards',
						id: 'cards'
					}
				],
				searchString: '',
				searchButton: {
					location: 'after',
					name: 'search',
					options: {
						icon: 'search',
						click: this.doSearch,
						stylingMode: 'text'
					}
				},
				gridHeight: null,
				selectedRowsData: [],
				initializedInfiniteloadException: false,
				gridWidth: 0,
				lastLoadedOpts: null,
				innerHeightDelta: this.heightDelta,
				innerPageSizeProp: null,
				pageSizes: [5, 10, 20, 50, 100],
				pageSizesDropDownOptions: { minWidth: 80 },
				editingSettings: { allowUpdating: false, allowAdding: false, allowDeleting: false, mode: 'popup' },
				sortingSettings: { mode: 'multiple' }
			};
		},
		computed: {
			columnChooserSettings() {
				return { 
					enabled: this.columnChooser
				};
			},
			keyboardNavigationSettings() {
				return { 
					enabled: !this.keyboardNavigationDisabled
				}
			},
			innerPageSize: {
				get: function() { 
					return this.showPageSize && this.innerPageSizeProp?.value || this.pageSize;
				},
				set: function(v) { 
					if (this.innerPageSizeProp) { 
						this.innerPageSizeProp.value = v;
					}
				}
			},
			cardsNoData() {
				return !this.isLoading && this.lastLoadedOpts && !this.items?.length;
			},
			pagesCount() {
				return Math.ceil(this.total / this.innerPageSize);
			},
			gridColumns() {
				if (!this.columns) return null;
				return this.columns.map(f => ({
					...f,
					caption: typeof (f.caption) === 'function' ? f.caption() : f.caption
				}));
			},
			listViewName() {
				return this.$parent?.$options?.name || '';
			}
		},
		watch: {
			pageIndex(v) {
				if (!this.isLoading)
					this.$innerReload();
				this.updateQueryString();
				this.$emit('update:currentPage', v);
			},
			pageSize(v) {
				this.innerPageSize = v;
			},
			innerPageSize() {
				if (!this.isLoading)
					this.$innerReload();
			},
			initialView(v) {
				this.view = v;
			},
			searchString() {
				this.$doSearch();
			},
			name() {
				if (!this.isLoading)
					this.$innerReload();
			},
			selectedRowKeys(v) {
				this.onGridContentReady();
			},
			pagesCount() {
				if (this.pageIndex > this.pagesCount - 1)
					this.pageIndex = Math.max(0, this.pagesCount - 1);
			},
			height() {
				this.calcSizes();
			},
		},
		mounted() {
			this.innerPageSizeProp = storage.prop(this.getStorageId(), void 0, this.pageSize);

			if (__DEBUG__) {
				console.debug('Mounted list view');
				window.listView = this;
			}

			this.$calcSizes = _.debounce(this.calcSizes, 1, {
				'leading': false,
				'trailing': true
			});

			this.$doSearch = _.debounce(this.doSearch, this.searchTimer, {
				'leading': false,
				'trailing': true
			});

			this.$innerReload = _.debounce(this.innerReload, 200, {
				'leading': false,
				'trailing': true
			});

			this.$unobserve = resizeObserver.observe(this, this.$el, this.$calcSizes);
			this.$unobserveGridHolder = resizeObserver.observe(this, this.$refs.gridHolder, this.$calcSizes);
			this.$calcSizes();

			this.readQueryStringParams();

			this.initialized = true;
			if (this.view !== 'table')
				this.loadData();

			this.onGridContentReady();
		},
		destroyed() {
			if (this.$unobserve)
				this.$unobserve();
			if (this.$unobserveGridHolder)
				this.$unobserveGridHolder();
			delete this.$calcSizes;
			delete this.$innerChanging;
		},
		methods: {
			getStorageId() {
				return [...(this.$parent?.$el?.classList || [])].join('.');
			},
			readQueryStringParams() {
				if (!this.listViewName) return;
				this.pageIndex = Math.max(parseInt(this.$route?.query[`view-${this.listViewName}-page`] || 1) - 1, 0);
			},
			updateQueryString() {
				if (!this.listViewName) return;
				let query = { ...(this.$route.query || {}) };
				query[`view-${this.listViewName}-page`] = this.pageIndex + 1;
				this.$router
					.replace({ query })
					.catch((e) => {
						if (__DEBUG__)
							console.debug('$router.replace', e);
					});
			},
			onGridContentReady() {
				if (this.selectedRowKeys?.length) {
					setImmediate(() => {
						if (this._isDestroyed) return;
						this.$innerChanging = true;
						let tableInst = this.$refs.table?.instance;
						if (!tableInst) return;

						let indexes = this.selectedRowKeys.map(rk => {
							let idx = tableInst.getRowIndexByKey(rk);
							return idx;
						}).filter(f => f > -1);

						this.$refs.table?.instance?.selectRowsByIndexes(indexes);
						setImmediate(() => this.$innerChanging = false);
					})
				}
			},
			onSelectionChanged(e) {
				if (this.$innerChanging) return;

				if (__DEBUG__)
					console.debug('On selection changed', e);
				this.selectedRowsData = (e && e.selectedRowsData || []);
				const getItems = (arr) => {
					return (arr || [])
						.map(f => typeof (f) === 'object' ? f : this.items.filter(item => item && item[this.valueExpr] === f)[0])
						.filter(f => f);
				};
				this.$emit('selection', this.selectedRowsData, {
					selected: getItems(e && e.currentSelectedRowKeys),
					deselected: getItems(e && e.currentDeselectedRowKeys)
				});
			},
			hasFilters() {
				let table = this.$refs.table && this.$refs.table.instance;
				return table && table.getCombinedFilter();
			},
			clearFilters() {
				if (this.$refs.table && this.$refs.table.instance)
					this.$refs.table.instance.clearFilter();
			},
			calcSizes(opts) {
				if (this._isDestroyed) return;
				if (this.height) {
					this.gridHeight = this.height - (this.showViewHeader ? $(this.$refs.header).height() : 0);
				} else {
					if (opts?.heightDelta) {
						this.innerHeightDelta = opts?.heightDelta;
					}
					if (this.$refs.gridHolder && this.autoFit)
						this.gridHeight = window.innerHeight - this.$refs.gridHolder.getBoundingClientRect().top - (this.$refs.pagerBottom?.offsetHeight || 0) - this.innerHeightDelta - 10;
				}
				if (__DEBUG__)
					console.debug('Calc sizes', { gridWidth: this.gridWidth, delta: $(this.$refs.gridHolder).width() - this.gridWidth });
				if (this.$refs.gridHolder)
					this.gridWidth = $(this.$refs.gridHolder).width();
			},
			doSearch() {
				if (__DEBUG__)
					console.debug('Do search');
				this.pageIndex = 0;
				this.innerReload();
			},
			loadData(opts) {
				opts = opts || {};
				if (!this.initialized || (this.view === 'table' && !this.gridWidth)) return Promise.resolve(null);
				if (opts?.filter && opts?.filter[0] === this.valueExpr) {
					return Promise.resolve(this.items?.filter(f => f[this.valueExpr] === opts?.filter[2]));
				}
				if (this.getData) {
					this.lastLoadedOpts = opts;
					this.isLoading = true;
					if (opts.clear) {
						this.pageIndex = 0;
					}

					return new Promise(resolve => {
						let result = typeof this.getData === 'function' ? this.getData(this.pageIndex, this.innerPageSize, opts) : {};
						if (typeof result.then === 'function') { // if getData is the Promise
							return result
								.then(r => {
									if (this._isDestroyed) return;
									this.loadedResult = r;
									this.total = this.getTotal(r);
									this.items = this.getItems(r);

									let maxPageIndex = Math.ceil(this.total / this.innerPageSize) - 1;
									if (this.pageIndex > maxPageIndex) {
										this.pageIndex = Math.max(0, this.pageIndex - 1);
										this.$innerReload();
									}

									if (__DEBUG__)
										console.debug('Get data', { pageIndex: this.pageIndex, total: this.total });

									resolve(r);
									this.$emit('data-loaded', r);
									return r;
								})
								.catch((ex) => {
									if (__DEBUG__) console.error(ex);
									this.total = this.total || 0;
									this.items = this.items || [];
									resolve(null);
								})
								.finally(() => this.isLoading = false);
						} else { // if getData is function
							this.loadedResult = result;
							this.total = this.getTotal ? this.getTotal(result) : result.length;
							this.items = this.getItems ? this.getItems(result) : result;
							resolve(result);
							this.$emit('data-loaded', result);
							this.isLoading = false;
							return result;
						}
					});
				}
				return Promise.resolve(null);
			},
			onPageChange(v) {
				this.pageIndex = Math.max(v, 0);
			},
			onCellClick() {

			},
			getSorting(native) {
				return (this.$refs.table && this.$refs.table.instance.getVisibleColumns() || [])
					.filter(f => f.sortOrder)
					.map(f => (native
						? {
							selector: f.serverField || f.dataField,
							desc: f.sortOrder === 'desc'
						}
						: {
							fieldName: f.serverField || f.dataField,
							direction: f.sortOrder
						}));
			},
			reload(opts) {
				if (__DEBUG__)
					console.debug('List view reload');

				opts = opts || {};
				if (opts.clear) {
					this.items = [];
					this.pageIndex = 0;

					if (this.$refs.table?.instance) {
						var grid = this.$refs.table?.instance;
						grid.option('dataSource', null);
						grid.option('dataSource', this.source);
					}
				}

				setImmediate(() => {
					this.$innerReload();
					this.$emit('reload');
				});
			},
			innerReload() {
				this.lastLoadedOpts = null;
				this.source.reload();
			},
			deselectAll() {
				if (this.$refs.table?.instance) {
					this.$refs.table.instance.deselectAll();
				}
			}
		}
	};
</script>

<style lang='scss'>
	.app-core--list-view {
		@import "main/themes/generated/variables.base.scss";

		.view-holder {
			position: relative;
		}

		.cards {
			min-height: 100px;
		}

		.card {
			flex: 1;
			margin: 10px;
		}

		.app-core--pager > .page.active {
			background: $base-accent;
			color: $base-bg;
		}

		.cards-loader {
			font-size: 32px;
			box-shadow: 2px 2px 13px 2px rgb(0,0,0,0.1);
			width: 55px;
			height: 55px;
			border-radius: 50px;
			background: #fff;
		}

		.grid-holder-parent.auto-fit {
			position: relative;
			height: var(--grid-height);

			.grid-holder {
				position: absolute;
				left: 0px;
				top: 0px;
				width: 100%;
				height: var(--grid-height);
			}
		}

		&.no-padding {
			.lr-padding {
				padding: 0px !important;
			}
		}

		.no-data {
			min-height: 200px;
		}

		.dx-datagrid-headers .dx-datagrid-table .dx-row > td {
			padding-top: 12px;
			padding-bottom: 12px;
		}

		.dx-datagrid-headers .dx-row.dx-datagrid-filter-row > td {
			padding-top: 0px;
			padding-bottom: 0px;
		}

		.page-size-selector {
			position: relative;

			> .caption {
				position: absolute;
				top: -4px;
				left: 0px;
				white-space: nowrap;
				font-size: 7px;
			}

			input {
				font-size: 14px !important;
				padding: 4px 0 4px !important;
			}
		}
	}
</style>
